import { compareTrackingOrders } from '@/lib/utils/tracking-order';
import { mockLocationOrders, mockLocationOrderItems } from '@/lib/mock-data-components';
import type {
  LocationOrder,
  LocationOrderItem,
  LocationOrderInsert,
  LocationOrderItemInsert,
  LocationOrderItemUpdate,
  LocationOrderUpdate,
} from '@/types/location-orders';
import { mockLocationLaunchesService } from './mock-location-launches';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const mockLocationOrdersService = {
  async getAll(): Promise<LocationOrder[]> {
    await delay(300);
    return [...mockLocationOrders];
  },

  async getById(id: string): Promise<LocationOrder | null> {
    await delay(200);
    return mockLocationOrders.find((lo) => lo.id === id) ?? null;
  },

  async getByLocationId(locationId: string): Promise<LocationOrder[]> {
    await delay(200);
    return mockLocationOrders.filter((lo) => lo.location_id === locationId).sort((a,b)=>b.order_date.localeCompare(a.order_date)||a.id.localeCompare(b.id));
  },

  async getByLaunchId(launchId: string): Promise<LocationOrder[]> {
    await delay(200);
    return mockLocationOrders.filter((lo) => lo.launch_id === launchId);
  },

  async create(data: LocationOrderInsert): Promise<LocationOrder> {
    await delay(400);
    const newOrder: LocationOrder = {
      id: `lo-${Date.now()}`,
      deal_id: null,
      launch_id: data.launch_id ?? null,
      status: 'pending',
      total_amount: null,
      paid_amount: null,
      paid_date: null,
      notes: null,
      created_by: null,
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockLocationOrders.push(newOrder);
    const { refreshAllMockPaidWorkAssociations } = await import('./mock-invoice-paid-work');
    await refreshAllMockPaidWorkAssociations();
    return newOrder;
  },

  async createFromDeal(deal: { id: string; location_id: string; total_contract_value: number; name: string }): Promise<LocationOrder> {
    await delay(400);
    // Check if order already exists for this deal (idempotency)
    const existingOrders = await this.getByDealId(deal.id);
    const existingOrder = existingOrders[0];
    if (existingOrder) {
      return existingOrder;
    }

    const today = new Date().toISOString().split('T')[0] || new Date().toISOString();
    const primaryLaunch = await mockLocationLaunchesService.getPrimaryByLocationId(deal.location_id);
    const newOrder: LocationOrder = {
      id: `lo-${Date.now()}`,
      location_id: deal.location_id,
      deal_id: deal.id,
      launch_id: primaryLaunch?.id ?? null,
      order_date: today,
      status: 'pending',
      total_amount: deal.total_contract_value,
      paid_amount: null,
      paid_date: null,
      notes: `Auto-created from closed deal: ${deal.name}`,
      created_by: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockLocationOrders.push(newOrder);
    const { refreshAllMockPaidWorkAssociations } = await import('./mock-invoice-paid-work');
    await refreshAllMockPaidWorkAssociations();
    return newOrder;
  },

  async getByDealId(dealId: string): Promise<LocationOrder[]> {
    await delay(200);
    return mockLocationOrders.filter((lo) => lo.deal_id === dealId);
  },

  async update(id: string, data: LocationOrderUpdate): Promise<LocationOrder> {
    await delay(400);
    const index = mockLocationOrders.findIndex((lo) => lo.id === id);
    if (index === -1) {
      throw new Error('Location order not found');
    }
    const existing = mockLocationOrders[index];
    if (!existing) {
      throw new Error('Location order not found');
    }
    const updated: LocationOrder = {
      ...existing,
      ...data,
      updated_at: new Date().toISOString(),
    };
    mockLocationOrders[index] = updated;
    if (existing.location_id !== updated.location_id) {
      const { refreshAllMockPaidWorkAssociations } = await import('./mock-invoice-paid-work');
      await refreshAllMockPaidWorkAssociations();
    }
    return updated;
  },

  async delete(id: string): Promise<void> {
    await delay(300);
    const index = mockLocationOrders.findIndex((lo) => lo.id === id);
    if (index !== -1) {
      mockLocationOrders.splice(index, 1);
      const { refreshAllMockPaidWorkAssociations } = await import('./mock-invoice-paid-work');
      await refreshAllMockPaidWorkAssociations();
    }
  },

  async getOrCreateTrackingOrderForLaunch(launchId: string): Promise<LocationOrder> {
    await delay(200);
    const existingOrders = mockLocationOrders
      .filter((order) => order.launch_id === launchId)
      .sort(compareTrackingOrders);
    const existingOrder = existingOrders[0];
    if (existingOrder) {
      return existingOrder;
    }

    const launch = await mockLocationLaunchesService.getById(launchId);
    if (!launch) {
      throw new Error('Launch not found');
    }

    const today = new Date().toISOString().split('T')[0] || new Date().toISOString();
    return this.create({
      location_id: launch.location_id,
      launch_id: launch.id,
      order_date: today,
      status: 'pending',
      notes: 'Created for launch fulfillment tracking',
    });
  },
};

export const mockLocationOrderItemsService = {
  async getByOrderId(orderId: string): Promise<LocationOrderItem[]> {
    await delay(200);
    return mockLocationOrderItems.filter((loi) => loi.order_id === orderId);
  },

  async getByLocationId(locationId: string): Promise<LocationOrderItem[]> {
    await delay(200);
    const orderIds = new Set(
      mockLocationOrders.filter((order) => order.location_id === locationId).map((order) => order.id)
    );
    return mockLocationOrderItems.filter((item) => orderIds.has(item.order_id));
  },

  async getById(id: string): Promise<LocationOrderItem | null> {
    await delay(200);
    return mockLocationOrderItems.find((loi) => loi.id === id) ?? null;
  },

  async create(data: LocationOrderItemInsert): Promise<LocationOrderItem> {
    await delay(300);
    const newItem: LocationOrderItem = {
      id: `loi-${Date.now()}`,
      unit_price: null,
      ...data,
      created_at: new Date().toISOString(),
    };
    mockLocationOrderItems.push(newItem);
    return newItem;
  },

  async update(id: string, data: LocationOrderItemUpdate): Promise<LocationOrderItem> {
    await delay(300);
    const index = mockLocationOrderItems.findIndex((item) => item.id === id);
    if (index === -1) {
      throw new Error('Location order item not found');
    }

    const existing = mockLocationOrderItems[index];
    if (!existing) {
      throw new Error('Location order item not found');
    }

    const updated: LocationOrderItem = {
      ...existing,
      ...data,
    };

    mockLocationOrderItems[index] = updated;
    return updated;
  },

  async delete(id: string): Promise<void> {
    await delay(200);
    const index = mockLocationOrderItems.findIndex((loi) => loi.id === id);
    if (index !== -1) {
      mockLocationOrderItems.splice(index, 1);
    }
  },

  async deleteByOrderId(orderId: string): Promise<void> {
    await delay(200);
    const indices = mockLocationOrderItems
      .map((loi, idx) => (loi.order_id === orderId ? idx : -1))
      .filter((idx) => idx !== -1)
      .reverse();
    indices.forEach((idx) => mockLocationOrderItems.splice(idx, 1));
  },
};
